import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getConversionIntent, clearConversionIntent, handlePostSignupConversion } from './conversionFlow';
import { createFileLogger } from '../../../../utils/logger';

const log = createFileLogger('CONVERSION_HANDLER');

export const ConversionHandler: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const checkForPendingConversion = async () => {
      // Check if user just signed up with conversion intent
      const urlParams = new URLSearchParams(window.location.search);
      const hasConversionIntent = urlParams.get('intent') === 'conversion';
      
      if (!hasConversionIntent) {
        return;
      }

      log.log('[ConversionHandler] Checking for pending conversion...');

      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        log.log('[ConversionHandler] No auth token found, skipping conversion');
        return;
      }

      const conversionIntent = getConversionIntent();
      if (!conversionIntent) {
        log.log('[ConversionHandler] No conversion intent found');
        return;
      }

      // Retrieve the file from sessionStorage
      const fileData = sessionStorage.getItem('pendingConversionFile');
      const fileName = sessionStorage.getItem('pendingConversionFileName');
      const fileType = sessionStorage.getItem('pendingConversionFileType');

      if (!fileData || !fileName) {
        log.error('[ConversionHandler] File data not found in sessionStorage');
        clearConversionIntent();
        return;
      }

      log.log('[ConversionHandler] Found pending conversion:', {
        fileName,
        fileType,
        type: conversionIntent.type,
      });

      // Convert base64 back to File
      const base64Data = fileData.split(',')[1];
      const byteString = atob(base64Data);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: fileType || 'application/pdf' });
      const file = new File([blob], fileName || 'document.pdf', { type: fileType || 'application/pdf' });

      // Get or create space
      try {
        log.log('[ConversionHandler] Fetching user spaces...');
        const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001';
        
        const spacesResponse = await fetch(`${API_BASE_URL}/api/spaces/recent`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
          },
        });

        if (!spacesResponse.ok) {
          throw new Error('Failed to fetch spaces');
        }

        const spaces = await spacesResponse.json();
        let spaceId = spaces[0]?.id || spaces[0]?._id;

        // If no space exists, create one
        if (!spaceId) {
          log.log('[ConversionHandler] No space found, creating one...');
          const createSpaceResponse = await fetch(`${API_BASE_URL}/api/spaces`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ name: 'My Documents' }),
          });

          if (!createSpaceResponse.ok) {
            throw new Error('Failed to create space');
          }

          const newSpace = await createSpaceResponse.json();
          spaceId = newSpace.id || newSpace._id;
          log.log('[ConversionHandler] Created new space:', spaceId);
        }

        // Process the conversion
        log.log('[ConversionHandler] Processing conversion...');
        const result = await handlePostSignupConversion(
          file,
          authToken,
          spaceId,
          conversionIntent.options
        );

        // Clean up
        sessionStorage.removeItem('pendingConversionFile');
        sessionStorage.removeItem('pendingConversionFileName');
        sessionStorage.removeItem('pendingConversionFileType');

        if (result.success && result.docId) {
          log.log('[ConversionHandler] Conversion successful, navigating to doc:', result.docId);
          navigate(`/spaces/${result.spaceId}?openDoc=${result.docId}&converted=true`, { replace: true });
        } else {
          log.error('[ConversionHandler] Conversion failed:', result.error);
          navigate(`/spaces/${spaceId}`, { replace: true });
        }
      } catch (error) {
        log.error('[ConversionHandler] Error processing conversion:', error);
        clearConversionIntent();
        sessionStorage.removeItem('pendingConversionFile');
        sessionStorage.removeItem('pendingConversionFileName');
        sessionStorage.removeItem('pendingConversionFileType');
      }
    };

    checkForPendingConversion();
  }, [navigate]);

  return null;
};

